<script setup lang="ts">
import type { CategoryNode } from "@/modules/api/gql/types/category";
import { ROUTE_BLOG_CATEGORY, ROUTE_BLOG_SEARCH } from "~/constants/routes";
import { useRouter } from "vue-router";
import { useFocus, useScroll } from "@vueuse/core";

const scrollContainer = ref<HTMLElement | null>(null);

const { arrivedState } = useScroll(scrollContainer, {
  behavior: "smooth"
});

const canScrollLeft = computed(() => !arrivedState.left);
const canScrollRight = computed(() => !arrivedState.right);

const SCROLL_AMOUNT = 240;

const scrollLeft = () => {
  scrollContainer.value?.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });

};

const scrollRight = () => {
  scrollContainer.value?.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
};


const route = useRoute();
const { replaceParams } = useRouteParams();

defineProps<{
  categories: CategoryNode[];
}>();

const currentCategory = computed(() => route.params.slug);
const router = useRouter();
const searchInput = shallowRef();
const { focused } = useFocus(searchInput, { initialValue: false });
const searchText = ref("");
const isShowSearch = ref(false);
const searchInputWrapper = ref(null);

const openSearch = () => {
  isShowSearch.value = true;
  focused.value = true;
  onClickOutside(searchInputWrapper, () => {
    closeSearch();
  });
};

const closeSearch = () => {
  isShowSearch.value = false;
  focused.value = false;
  searchText.value = "";
};

const submitSearch = async () => {
  if (searchText.value) {
    router.push({ path: ROUTE_BLOG_SEARCH, query: { q: searchText.value } });
  }
};
</script>

<template>
  <!-- PAGE WRAPPER -->
  <div class="mt-4 w-full flex justify-center">
    <!-- CENTERED CELERY BAR -->
    <div
      class="relative
             h-12
             max-w-[1100px]
             w-[90vw]
             rounded-[50px]
             bg-tertiary-misc-celery
             shadow-nl-19
             mb-8
             flex items-center border-solid border-4 border-tertiary-misc-celery">
      <!-- SCROLLABLE INNER CONTENT -->
      <div
        ref="scrollContainer" :class="[
          'scroll-mask flex items-center whitespace-nowrap scrollbar-thin h-full overflow-y-hidden',
          isShowSearch ? 'overflow-x-hidden' : 'overflow-x-auto',
          canScrollLeft && canScrollRight && 'mask-both',
          canScrollLeft && !canScrollRight && 'mask-left',
          !canScrollLeft && canScrollRight && 'mask-right'
        ]">
        <button
          type="button" aria-label="Search the blog"
          class="icon-main-search p-[14px] text-[19px] flex items-center justify-center cursor-pointer"
          @click="openSearch" />
        <div v-if="categories.length > 0" class="flex items-center md:ml-2.5">
          <ul class="flex items-center gap-2 h-full">
            <li
              v-for="category in categories" :key="category.id" class="flex items-center
                     [&.active]:bg-primary-cream-100
                     hover:bg-primary-cream-100
                     transition-colors
                     rounded-[50px]" :class="{ active: category.slug === currentCategory }">
              <nuxt-link
                :to="{ path: replaceParams(ROUTE_BLOG_CATEGORY, { slug: category.slug }) }" class="flex items-center justify-center
                       h-full
                       px-2.5 sm:px-[23px]
                       text-desktop-button-m
                       text-primary-kale-500 py-3
                       hover:text-primary-spinach-500
                       [&.router-link-active]:text-primary-kale-500
                       transition-all">
                {{ category.name }}
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>
      <form
        data-algolia-search-form role="search" method="get"
        :class="{ active: isShowSearch }"
        class="search-form absolute self-center m-auto overflow-hidden w-0 transition-all duration-300 flex flex-row ease-linear z-[2] bg-tertiary-misc-celery rounded-[50px] flex items-center justify-center h-12 [&.active]:w-full [&.active]:visible"
        @submit.prevent="submitSearch">
        <div
          ref="searchInputWrapper"
          class="sm:max-w-[372px] w-full flex bg-primary-cream-100 padding-1 rounded-[50px] relative mx-[13px]">
          <i
            class="icon-main-times z-1 absolute top-2 left-2.5 cursor-pointer text-nl-green-100"
            @click="closeSearch" />
          <input
            ref="searchInput" v-model="searchText" type="text" aria-label="Search input"
            data-algolia-search-input
            class="flex-1 py-1 w-full bg-transparent text-nl-green-100 font-desktop-body-xl pl-[42px] tracking-[0.08em] min-w-0 placeholder-primary-kale-200 focus:outline-none"
            placeholder="Search Blog">
          <input
            id="searchsubmit" data-algolia-submit-input type="submit"
            class="bg-tertiary-banana-500 hover:bg-tertiary-banana-400 hover:cursor-pointer text-nl-green-100 text-desktop-button-s rounded-[50px] w-[106px]"
            value="Search">
        </div>
      </form>
      <span v-if="canScrollLeft && !isShowSearch" class="absolute left-2 z-20 scroll-backward">
        <button type="button" class="flex items-center justify-center" @click="scrollLeft">
          <svg width="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd" clip-rule="evenodd"
              d="M15.2071 6.29289C15.5976 6.68342 15.5976 7.31658 15.2071 7.70711L10.9142 12L15.2071 16.2929C15.5976 16.6834 15.5976 17.3166 15.2071 17.7071C14.8166 18.0976 14.1834 18.0976 13.7929 17.7071L8.79289 12.7071C8.40237 12.3166 8.40237 11.6834 8.79289 11.2929L13.7929 6.29289C14.1834 5.90237 14.8166 5.90237 15.2071 6.29289Z"
              fill="#004b2c" />
          </svg>
        </button>
      </span>
      <span v-if="canScrollRight && !isShowSearch" class="absolute right-2 z-20 scroll-forward">
        <button type="button" class="flex items-center justify-center" @click="scrollRight">
          <svg width="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd" clip-rule="evenodd"
              d="M8.79289 6.29289C9.18342 5.90237 9.81658 5.90237 10.2071 6.29289L15.2071 11.2929C15.5976 11.6834 15.5976 12.3166 15.2071 12.7071L10.2071 17.7071C9.81658 18.0976 9.18342 18.0976 8.79289 17.7071C8.40237 17.3166 8.40237 16.6834 8.79289 16.2929L13.0858 12L8.79289 7.70711C8.40237 7.31658 8.40237 6.68342 8.79289 6.29289Z"
              fill="#004b2c" />
          </svg> </button>

      </span>
    </div>
  </div>
</template>

<style scoped>
/* put in global CSS */
.scrollbar-thin::-webkit-scrollbar {
  display: none;
}

.scroll-backward,
.scroll-forward {
  transition: opacity 0.2s ease;
}

.scroll-mask {
  -webkit-mask-image: linear-gradient(to right,
      transparent 0%,
      black 10%,
      black 90%,
      transparent 100%);
  mask-image: linear-gradient(to right,
      transparent 0%,
      black 10%,
      black 90%,
      transparent 100%);
}

.mask-left {
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 22%);
  mask-image: linear-gradient(to right, transparent 0%, black 22%);
}

.mask-right {
  -webkit-mask-image: linear-gradient(to left, transparent 6%, black 33%);
  mask-image: linear-gradient(to left, transparent 6%, black 33%);
}

.mask-both {
  -webkit-mask-image: linear-gradient(to right,
      transparent 6%,
      black 33%,
      black 67%,
      transparent 94%);
  mask-image: linear-gradient(to right,
      transparent 6%,
      black 33%,
      black 67%,
      transparent 94%);
}
</style>
